// src/constants/accessGuard.ts
var ACCESS_GUARD = {
  UNREGISTERED_COURSE_ACCESS: "unregisteredCourseAccess",
  UNREGISTERED_QA_ACCESS: "unregisteredQAAccess",
  UNREGISTERED_NEWS_ACCESS: "unregisteredNewsAccess",
  UNREGISTERED_ARTICLES_ACCESS: "unregisteredArticlesAccess"
};

// src/constants/aiMentorAvatar.ts
var ALLOWED_AVATAR_IMAGE_TYPES = ["image/jpeg", "image/png", "image/svg+xml", "image/jpg"];
var ALLOWED_EXTENSIONS = ".jpeg,.png,.svg,.jpg";

// src/constants/aiMentorTypes.ts
var AI_MENTOR_TYPE = {
  MENTOR: "mentor",
  TEACHER: "teacher",
  ROLEPLAY: "roleplay"
};

// src/constants/aiMentorVoice.ts
var AI_MENTOR_VOICE_MODE = {
  PRESET: "preset",
  CUSTOM: "custom"
};
var AI_MENTOR_TTS_PRESET = {
  MALE: "male",
  FEMALE: "female"
};

// src/constants/allowedAge.ts
var ALLOWED_AGE_LIMITS = [13, 16, null];

// src/constants/announcements.ts
var ANNOUNCEMENT_STATUSES = {
  SCHEDULED: "scheduled",
  PUBLISHED: "published"
};
var ANNOUNCEMENT_SOURCE_TYPES = {
  MANUAL: "manual",
  LIVE_TRAINING: "live_training"
};
var ANNOUNCEMENT_EMAIL_TEMPLATES = {
  DEFAULT: "default",
  LIVE_TRAINING_REMINDER: "live_training_reminder",
  LIVE_TRAINING_STARTED: "live_training_started",
  LIVE_TRAINING_ENDED: "live_training_ended"
};

// src/constants/articlesSettings.ts
var ALLOWED_ARTICLES_SETTINGS = {
  ARTICLES_ENABLED: "articlesEnabled",
  UNREGISTERED_USER_ARTICLES_ACCESSIBILITY: "unregisteredUserArticlesAccessibility"
};

// src/constants/articlesTypes.ts
var ARTICLE_STATUS = {
  PUBLISHED: "published",
  DRAFT: "draft"
};

// src/constants/calendar.ts
var CALENDAR_EVENT_SOURCE_TYPES = {
  LIVE_TRAINING: "live_training"
};
var CALENDAR_EVENT_SOURCE_ROLES = {
  ADMIN: "admin",
  AUTHOR: "author",
  TRAINER: "trainer",
  OBSERVER: "observer"
};

// src/constants/certificate.ts
var SHARE_IMAGE_WIDTH = 1200;
var SHARE_IMAGE_HEIGHT = Math.round(SHARE_IMAGE_WIDTH * 210 / 297);
var CERTIFICATE_STATUSES = {
  ACTIVE: "active",
  ARCHIVED: "archived"
};
var CERTIFICATE_ARCHIVE_REASONS = {
  EXPIRED: "expired",
  MANUAL_RESET: "manual_reset"
};
var CERTIFICATE_VALIDITY_TYPES = {
  PERIOD: "period",
  FIXED_DATE: "fixedDate"
};
var CERTIFICATE_VALIDITY_UNITS = {
  DAYS: "days",
  MONTHS: "months",
  YEARS: "years"
};
var CERTIFICATE_RESET_SCOPES = {
  ALL: "all",
  GROUPS: "groups",
  USERS: "users"
};

// src/constants/course.ts
var COURSE_STATUSES = {
  DRAFT: "draft",
  PUBLISHED: "published",
  PRIVATE: "private"
};
var COURSE_PROGRESS_STATUSES = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
  BLOCKED: "blocked"
};
var COURSE_TYPE = {
  DEFAULT: "default",
  SCORM: "scorm"
};
var COURSE_FEATURE = {
  CURRICULUM_EDITING: "curriculum_editing",
  LESSON_SEQUENCE_SETTING: "lesson_sequence_setting",
  QUIZ_FEEDBACK_SETTING: "quiz_feedback_setting"
};
var COURSE_FEATURE_ERROR_TRANSLATION_KEY = {
  [COURSE_FEATURE.CURRICULUM_EDITING]: "adminCourseView.errors.featureUnavailable.curriculum",
  [COURSE_FEATURE.LESSON_SEQUENCE_SETTING]: "adminCourseView.errors.featureUnavailable.lessonSequence",
  [COURSE_FEATURE.QUIZ_FEEDBACK_SETTING]: "adminCourseView.errors.featureUnavailable.quizFeedback"
};
var COURSE_TYPE_DISABLED_FEATURES = {
  [COURSE_TYPE.DEFAULT]: [],
  [COURSE_TYPE.SCORM]: [
    COURSE_FEATURE.CURRICULUM_EDITING,
    COURSE_FEATURE.LESSON_SEQUENCE_SETTING,
    COURSE_FEATURE.QUIZ_FEEDBACK_SETTING
  ]
};
var isCourseFeatureEnabledForCourseType = (courseType, feature) => {
  const disabledFeatures = COURSE_TYPE_DISABLED_FEATURES[courseType];
  return !disabledFeatures.includes(feature);
};

// src/constants/courseChat.ts
var COURSE_CHAT_SOCKET_EVENTS = {
  JOIN: "join:course-chat",
  LEAVE: "leave:course-chat",
  MESSAGE_CREATED: "course-chat:message-created",
  USER_PRESENCE_CHANGED: "course-chat:user-presence-changed",
  USER_MENTIONED: "course-chat:user-mentioned",
  MESSAGE_REACTIONS_UPDATED: "course-chat:message-reactions-updated",
  MESSAGE_DELETED: "course-chat:message-deleted"
};
var COURSE_CHAT_ALLOWED_REACTIONS = ["\u{1F44D}", "\u2764\uFE0F", "\u{1F602}", "\u{1F389}", "\u{1F62E}"];
var getCourseChatRoom = (courseId) => `course-chat:${courseId}`;

// src/constants/courseEnrollment.ts
var COURSE_ENROLLMENT = {
  ENROLLED: "enrolled",
  NOT_ENROLLED: "not_enrolled",
  GROUP_ENROLLED: "group_enrolled"
};

// src/constants/entityTypes.ts
var ENTITY_TYPES = {
  COURSE: "course",
  CHAPTER: "chapter",
  LESSON: "lesson",
  QUESTION: "question",
  NEWS: "news",
  ARTICLES: "articles",
  USER: "user",
  CATEGORY: "category",
  ANNOUNCEMENT: "announcement",
  GLOBAL_SETTINGS: "global_settings",
  LIVE_TRAINING: "live_training"
};

// src/constants/features.ts
var FEATURES = {
  QA: "qa",
  NEWS: "news",
  ARTICLES: "articles",
  MODERN_COURSE_LIST: "modern_course_list",
  CALENDAR: "calendar",
  LIVE_TRAINING: "live_training",
  COURSE_DISCUSSIONS: "course_discussions"
};
var FEATURE_SETTINGS_KEYS = {
  [FEATURES.QA]: "QAEnabled",
  [FEATURES.NEWS]: "newsEnabled",
  [FEATURES.ARTICLES]: "articlesEnabled",
  [FEATURES.MODERN_COURSE_LIST]: "modernCourseListEnabled",
  [FEATURES.CALENDAR]: "calendarEnabled",
  [FEATURES.LIVE_TRAINING]: "liveTrainingEnabled",
  [FEATURES.COURSE_DISCUSSIONS]: "courseDiscussionsEnabled"
};

// src/constants/fileTypes.ts
var ALLOWED_LESSON_IMAGE_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
  "image/tiff"
];
var ALLOWED_VIDEO_FILE_TYPES = [
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "video/ogg",
  "video/avi",
  "video/mov",
  "video/wmv"
];
var ALLOWED_PRESENTATION_FILE_TYPES = [
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.presentation"
];
var ALLOWED_EXCEL_FILE_TYPES = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv"
];
var ALLOWED_WORD_FILE_TYPES = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
var ALLOWED_PDF_FILE_TYPES = ["application/pdf"];
var ALLOWED_CERTIFICATE_SIGNATURE_FILE_TYPES = [
  "image/png",
  "image/svg+xml",
  "application/xml"
];

// src/constants/languages.ts
var SUPPORTED_LANGUAGES = {
  EN: "en",
  PL: "pl",
  DE: "de",
  LT: "lt",
  CS: "cs"
};

// src/constants/learningPath.ts
var LEARNING_PATH_STATUSES = {
  DRAFT: "draft",
  PUBLISHED: "published",
  PRIVATE: "private"
};
var LEARNING_PATH_ENROLLMENT_TYPES = {
  DIRECT: "direct",
  GROUP: "group"
};
var LEARNING_PATH_PROGRESS_STATUSES = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed"
};
var LEARNING_PATH_ENTITY_TYPES = {
  LEARNING_PATH: "learning_path",
  COURSE: "course"
};
var LEARNING_PATH_CERTIFICATE_STATUSES = {
  ACTIVE: "active",
  EXPIRED: "expired"
};

// src/constants/lessonTypes.ts
var LESSON_TYPES = {
  CONTENT: "content",
  QUIZ: "quiz",
  AI_MENTOR: "ai_mentor",
  EMBED: "embed",
  SCORM: "scorm",
  LIVE_TRAINING: "live_training"
};

// src/constants/liveTraining.ts
var CALENDAR_EVENT_STATUSES = {
  SCHEDULED: "scheduled",
  CANCELLED: "cancelled",
  ENDED: "ended",
  EXPIRED: "expired"
};
var LIVE_TRAINING_DELIVERY_TYPES = {
  ONLINE: "online",
  OFFLINE: "offline"
};
var LIVE_TRAINING_VISIBILITY_SCOPES = {
  ALL: "all",
  LINKED_COURSES: "linked_courses"
};
var LIVE_TRAINING_STATUSES = {
  SCHEDULED: "scheduled",
  ACTIVE: "active",
  ENDED: "ended",
  CANCELLED: "cancelled",
  EXPIRED: "expired"
};
var LIVE_TRAINING_SESSION_STATUSES = {
  WAITING: "waiting",
  ACTIVE: "active",
  ENDED: "ended",
  FAILED: "failed"
};
var LIVE_TRAINING_MEMBER_ROLES = {
  HOST: "host",
  CO_TRAINER: "co_trainer",
  MODERATOR: "moderator",
  OBSERVER: "observer"
};
var LIVE_TRAINING_PARTICIPANT_ROLES = {
  ...LIVE_TRAINING_MEMBER_ROLES,
  ADMIN: "admin"
};
var LIVE_TRAINING_LINK_ENTITY_TYPES = {
  COURSE: "course"
};
var LIVE_TRAINING_RESOURCE_RELATIONSHIP_TYPES = {
  BEFORE: "live_training_before",
  AFTER: "live_training_after"
};
var DEFAULT_LIVE_TRAINING_SETTINGS = {
  viewerPermissions: {
    microphoneEnabled: false,
    cameraEnabled: false
  }
};
var LIVE_TRAINING_MAX_PARTICIPANTS_LIMIT = 100;
var LIVE_TRAINING_TITLE_MAX_LENGTH = 120;
var LIVE_TRAINING_DESCRIPTION_MAX_LENGTH = 500;
var LIVE_TRAINING_SOCKET_ROOMS = {
  LIVE_TRAINING: "live-training",
  LIVE_TRAINING_SESSION: "live-training-session",
  LIVE_TRAINING_USER: "live-training-user"
};
var LIVE_TRAINING_SOCKET_CLIENT_EVENTS = {
  SUBSCRIBE: "liveTraining:subscribe",
  UNSUBSCRIBE: "liveTraining:unsubscribe",
  SESSION_START: "liveTraining:session:start",
  SESSION_END: "liveTraining:session:end",
  PARTICIPANT_HEARTBEAT: "liveTraining:participant:heartbeat",
  PARTICIPANT_LEAVE: "liveTraining:participant:leave"
};
var LIVE_TRAINING_SOCKET_SERVER_EVENTS = {
  SESSION_STARTED: "liveTraining:session:started",
  SESSION_ENDED: "liveTraining:session:ended",
  SESSION_STATUS_CHANGED: "liveTraining:session:statusChanged",
  PARTICIPANT_JOINED: "liveTraining:participant:joined",
  PARTICIPANT_LEFT: "liveTraining:participant:left",
  PARTICIPANT_UPDATED: "liveTraining:participant:updated",
  ATTENDANCE_UPDATED: "liveTraining:attendance:updated",
  POPUP_AVAILABLE: "liveTraining:popup:available",
  ERROR: "liveTraining:error"
};
var LIVE_TRAINING_SOCKET_ROOM_BUILDERS = {
  liveTraining: (liveTrainingId) => `${LIVE_TRAINING_SOCKET_ROOMS.LIVE_TRAINING}:${liveTrainingId}`,
  liveTrainingSession: (sessionId) => `${LIVE_TRAINING_SOCKET_ROOMS.LIVE_TRAINING_SESSION}:${sessionId}`,
  liveTrainingUser: (userId) => `${LIVE_TRAINING_SOCKET_ROOMS.LIVE_TRAINING_USER}:${userId}`
};

// src/constants/loginPageDocuments.ts
var MAX_LOGIN_PAGE_DOCUMENTS = 3;
var LOGIN_PAGE_DOCUMENTS_FILE_TYPES = ["application/pdf"];

// src/constants/lumaFileIngestion.ts
var LUMA_FILE_INGESTION_MAX_SIZE_MB = 10;
var LUMA_FILE_INGESTION_MAX_SIZE_BYTES = LUMA_FILE_INGESTION_MAX_SIZE_MB * 1024 * 1024;
var LUMA_FILE_INGESTION_ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
  "text/x-markdown",
  "application/x-markdown"
];
var LUMA_FILE_INGESTION_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".txt",
  ".md",
  ".xmd"
];

// src/constants/masterCourse.ts
var COURSE_ORIGIN_TYPES = {
  REGULAR: "regular",
  MASTER: "master",
  EXPORTED: "exported"
};
var MASTER_COURSE_EXPORT_SYNC_STATUSES = {
  ACTIVE: "active",
  FAILED: "failed",
  PAUSED: "paused"
};
var MASTER_COURSE_ENTITY_TYPES = {
  COURSE: "course",
  CHAPTER: "chapter",
  LESSON: "lesson",
  QUESTION: "question",
  OPTION: "option",
  RESOURCE: "resource",
  RESOURCE_ENTITY: "resource_entity",
  AI_MENTOR_LESSON: "ai_mentor_lesson"
};

// src/constants/newsSettings.ts
var ALLOWED_NEWS_SETTINGS = {
  NEWS_ENABLED: "newsEnabled",
  UNREGISTERED_USER_NEWS_ACCESSIBILITY: "unregisteredUserNewsAccessibility"
};

// src/constants/permissions.ts
var SYSTEM_ROLE_SLUGS = {
  ADMIN: "admin",
  STUDENT: "student",
  CONTENT_CREATOR: "content_creator",
  TRAINER: "trainer"
};
var PERMISSIONS = {
  ACCOUNT_READ_SELF: "account.read_self",
  ACCOUNT_UPDATE_SELF: "account.update_self",
  USER_READ_SELF: "user.read_self",
  USER_MANAGE: "user.manage",
  SETTINGS_READ_SELF: "settings.read_self",
  SETTINGS_UPDATE_SELF: "settings.update_self",
  SETTINGS_MANAGE: "settings.manage",
  ENV_READ_PUBLIC: "env.read_public",
  ENV_MANAGE: "env.manage",
  CATEGORY_READ: "category.read",
  CATEGORY_MANAGE: "category.manage",
  GROUP_READ: "group.read",
  GROUP_MANAGE: "group.manage",
  LEARNING_PATH_READ: "learning_path.read",
  LEARNING_PATH_CREATE: "learning_path.create",
  LEARNING_PATH_UPDATE: "learning_path.update",
  LEARNING_PATH_UPDATE_OWN: "learning_path.update_own",
  LEARNING_PATH_DELETE: "learning_path.delete",
  LEARNING_PATH_COURSE_UPDATE: "learning_path.course_update",
  LEARNING_PATH_COURSE_UPDATE_OWN: "learning_path.course_update_own",
  LEARNING_PATH_ENROLLMENT: "learning_path.enrollment",
  LEARNING_PATH_EXPORT: "learning_path.export",
  CALENDAR_READ: "calendar.read",
  LIVE_TRAINING_READ: "live_training.read",
  LIVE_TRAINING_CREATE: "live_training.create",
  LIVE_TRAINING_UPDATE: "live_training.update",
  LIVE_TRAINING_UPDATE_OWN: "live_training.update_own",
  LIVE_TRAINING_DELETE: "live_training.delete",
  LIVE_TRAINING_DELETE_OWN: "live_training.delete_own",
  LIVE_TRAINING_JOIN: "live_training.join",
  LIVE_TRAINING_START: "live_training.start",
  LIVE_TRAINING_END: "live_training.end",
  LIVE_TRAINING_STATISTICS: "live_training.statistics",
  COURSE_READ_ASSIGNED: "course.read_assigned",
  COURSE_READ_MANAGEABLE: "course.read_manageable",
  COURSE_READ: "course.read",
  COURSE_CREATE: "course.create",
  COURSE_UPDATE: "course.update",
  COURSE_UPDATE_OWN: "course.update_own",
  COURSE_DELETE: "course.delete",
  COURSE_ENROLLMENT: "course.enrollment",
  COURSE_STATISTICS: "course.statistics",
  COURSE_EXPORT: "course.export",
  COURSE_DISCUSSION_READ: "course_discussion.read",
  COURSE_DISCUSSION_MESSAGE_CREATE: "course_discussion.message.create",
  COURSE_DISCUSSION_MESSAGE_REACT: "course_discussion.message.react",
  COURSE_DISCUSSION_MESSAGE_DELETE_OWN: "course_discussion.message.delete_own",
  COURSE_DISCUSSION_MESSAGE_DELETE: "course_discussion.message.delete",
  LEARNING_MODE_USE: "learning_mode.use",
  LEARNING_PROGRESS_UPDATE: "learning_progress.update",
  CERTIFICATE_READ: "certificate.read",
  CERTIFICATE_SHARE: "certificate.share",
  CERTIFICATE_RENDER: "certificate.render",
  FILE_UPLOAD: "file.upload",
  FILE_DELETE: "file.delete",
  AI_USE: "ai.use",
  ANNOUNCEMENT_READ: "announcement.read",
  ANNOUNCEMENT_CREATE: "announcement.create",
  ANNOUNCEMENT_DELETE: "announcement.delete",
  NEWS_READ_PUBLIC: "news.read_public",
  NEWS_MANAGE: "news.manage",
  NEWS_MANAGE_OWN: "news.manage_own",
  ARTICLE_READ_PUBLIC: "article.read_public",
  ARTICLE_MANAGE: "article.manage",
  ARTICLE_MANAGE_OWN: "article.manage_own",
  QA_READ_PUBLIC: "qa.read_public",
  QA_MANAGE: "qa.manage",
  QA_MANAGE_OWN: "qa.manage_own",
  REPORT_READ: "report.read",
  STATISTICS_READ_SELF: "statistics.read_self",
  STATISTICS_READ: "statistics.read",
  BILLING_CHECKOUT: "billing.checkout",
  BILLING_MANAGE: "billing.manage",
  INTEGRATION_KEY_MANAGE: "integration_key.manage",
  INTEGRATION_API_USE: "integration_api.use",
  TENANT_MANAGE: "tenant.manage",
  COURSE_AI_GENERATION: "course.ai_generation",
  ACTIVITY_LOG_READ: "activity_log.read"
};
var SYSTEM_ROLE_PERMISSIONS = {
  [SYSTEM_ROLE_SLUGS.STUDENT]: [
    PERMISSIONS.ACCOUNT_READ_SELF,
    PERMISSIONS.ACCOUNT_UPDATE_SELF,
    PERMISSIONS.USER_READ_SELF,
    PERMISSIONS.SETTINGS_READ_SELF,
    PERMISSIONS.SETTINGS_UPDATE_SELF,
    PERMISSIONS.ENV_READ_PUBLIC,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.COURSE_READ_ASSIGNED,
    PERMISSIONS.COURSE_READ,
    PERMISSIONS.COURSE_DISCUSSION_READ,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_CREATE,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_REACT,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_DELETE_OWN,
    PERMISSIONS.LEARNING_PATH_READ,
    PERMISSIONS.CALENDAR_READ,
    PERMISSIONS.LIVE_TRAINING_READ,
    PERMISSIONS.LIVE_TRAINING_JOIN,
    PERMISSIONS.LEARNING_PROGRESS_UPDATE,
    PERMISSIONS.CERTIFICATE_READ,
    PERMISSIONS.CERTIFICATE_SHARE,
    PERMISSIONS.CERTIFICATE_RENDER,
    PERMISSIONS.FILE_UPLOAD,
    PERMISSIONS.AI_USE,
    PERMISSIONS.ANNOUNCEMENT_READ,
    PERMISSIONS.STATISTICS_READ_SELF,
    PERMISSIONS.BILLING_CHECKOUT,
    PERMISSIONS.NEWS_READ_PUBLIC,
    PERMISSIONS.ARTICLE_READ_PUBLIC,
    PERMISSIONS.QA_READ_PUBLIC
  ],
  [SYSTEM_ROLE_SLUGS.CONTENT_CREATOR]: [
    PERMISSIONS.ACCOUNT_READ_SELF,
    PERMISSIONS.ACCOUNT_UPDATE_SELF,
    PERMISSIONS.USER_READ_SELF,
    PERMISSIONS.SETTINGS_READ_SELF,
    PERMISSIONS.SETTINGS_UPDATE_SELF,
    PERMISSIONS.ENV_READ_PUBLIC,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.COURSE_READ_ASSIGNED,
    PERMISSIONS.COURSE_READ_MANAGEABLE,
    PERMISSIONS.COURSE_READ,
    PERMISSIONS.COURSE_DISCUSSION_READ,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_CREATE,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_REACT,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_DELETE_OWN,
    PERMISSIONS.LEARNING_PATH_READ,
    PERMISSIONS.LEARNING_PATH_CREATE,
    PERMISSIONS.LEARNING_PATH_UPDATE_OWN,
    PERMISSIONS.LEARNING_PATH_COURSE_UPDATE_OWN,
    PERMISSIONS.CALENDAR_READ,
    PERMISSIONS.LIVE_TRAINING_READ,
    PERMISSIONS.LIVE_TRAINING_JOIN,
    PERMISSIONS.LEARNING_PROGRESS_UPDATE,
    PERMISSIONS.LEARNING_MODE_USE,
    PERMISSIONS.COURSE_CREATE,
    PERMISSIONS.COURSE_UPDATE_OWN,
    PERMISSIONS.COURSE_STATISTICS,
    PERMISSIONS.CERTIFICATE_READ,
    PERMISSIONS.CERTIFICATE_SHARE,
    PERMISSIONS.CERTIFICATE_RENDER,
    PERMISSIONS.FILE_UPLOAD,
    PERMISSIONS.FILE_DELETE,
    PERMISSIONS.AI_USE,
    PERMISSIONS.COURSE_AI_GENERATION,
    PERMISSIONS.ANNOUNCEMENT_READ,
    PERMISSIONS.ANNOUNCEMENT_CREATE,
    PERMISSIONS.NEWS_MANAGE_OWN,
    PERMISSIONS.NEWS_READ_PUBLIC,
    PERMISSIONS.ARTICLE_MANAGE_OWN,
    PERMISSIONS.ARTICLE_READ_PUBLIC,
    PERMISSIONS.QA_MANAGE_OWN,
    PERMISSIONS.QA_READ_PUBLIC,
    PERMISSIONS.REPORT_READ,
    PERMISSIONS.STATISTICS_READ_SELF,
    PERMISSIONS.STATISTICS_READ,
    PERMISSIONS.BILLING_CHECKOUT
  ],
  [SYSTEM_ROLE_SLUGS.TRAINER]: [
    PERMISSIONS.ACCOUNT_READ_SELF,
    PERMISSIONS.ACCOUNT_UPDATE_SELF,
    PERMISSIONS.USER_READ_SELF,
    PERMISSIONS.SETTINGS_READ_SELF,
    PERMISSIONS.SETTINGS_UPDATE_SELF,
    PERMISSIONS.ENV_READ_PUBLIC,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.CALENDAR_READ,
    PERMISSIONS.LIVE_TRAINING_READ,
    PERMISSIONS.LIVE_TRAINING_CREATE,
    PERMISSIONS.LIVE_TRAINING_UPDATE_OWN,
    PERMISSIONS.LIVE_TRAINING_DELETE_OWN,
    PERMISSIONS.LIVE_TRAINING_JOIN,
    PERMISSIONS.LIVE_TRAINING_START,
    PERMISSIONS.LIVE_TRAINING_END,
    PERMISSIONS.LIVE_TRAINING_STATISTICS,
    PERMISSIONS.CERTIFICATE_READ,
    PERMISSIONS.CERTIFICATE_SHARE,
    PERMISSIONS.CERTIFICATE_RENDER,
    PERMISSIONS.FILE_UPLOAD,
    PERMISSIONS.ANNOUNCEMENT_READ
  ],
  [SYSTEM_ROLE_SLUGS.ADMIN]: [
    PERMISSIONS.ACCOUNT_READ_SELF,
    PERMISSIONS.ACCOUNT_UPDATE_SELF,
    PERMISSIONS.USER_READ_SELF,
    PERMISSIONS.SETTINGS_READ_SELF,
    PERMISSIONS.SETTINGS_UPDATE_SELF,
    PERMISSIONS.SETTINGS_MANAGE,
    PERMISSIONS.ENV_READ_PUBLIC,
    PERMISSIONS.ENV_MANAGE,
    PERMISSIONS.CATEGORY_READ,
    PERMISSIONS.CATEGORY_MANAGE,
    PERMISSIONS.GROUP_READ,
    PERMISSIONS.GROUP_MANAGE,
    PERMISSIONS.LEARNING_PATH_READ,
    PERMISSIONS.LEARNING_PATH_CREATE,
    PERMISSIONS.LEARNING_PATH_UPDATE,
    PERMISSIONS.LEARNING_PATH_DELETE,
    PERMISSIONS.LEARNING_PATH_COURSE_UPDATE,
    PERMISSIONS.LEARNING_PATH_ENROLLMENT,
    PERMISSIONS.LEARNING_PATH_EXPORT,
    PERMISSIONS.CALENDAR_READ,
    PERMISSIONS.LIVE_TRAINING_READ,
    PERMISSIONS.LIVE_TRAINING_CREATE,
    PERMISSIONS.LIVE_TRAINING_UPDATE,
    PERMISSIONS.LIVE_TRAINING_UPDATE_OWN,
    PERMISSIONS.LIVE_TRAINING_DELETE,
    PERMISSIONS.LIVE_TRAINING_DELETE_OWN,
    PERMISSIONS.LIVE_TRAINING_JOIN,
    PERMISSIONS.LIVE_TRAINING_START,
    PERMISSIONS.LIVE_TRAINING_END,
    PERMISSIONS.LIVE_TRAINING_STATISTICS,
    PERMISSIONS.COURSE_READ_ASSIGNED,
    PERMISSIONS.COURSE_READ_MANAGEABLE,
    PERMISSIONS.COURSE_READ,
    PERMISSIONS.COURSE_DISCUSSION_READ,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_CREATE,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_REACT,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_DELETE_OWN,
    PERMISSIONS.COURSE_DISCUSSION_MESSAGE_DELETE,
    PERMISSIONS.LEARNING_MODE_USE,
    PERMISSIONS.COURSE_CREATE,
    PERMISSIONS.COURSE_UPDATE,
    PERMISSIONS.COURSE_DELETE,
    PERMISSIONS.COURSE_STATISTICS,
    PERMISSIONS.COURSE_ENROLLMENT,
    PERMISSIONS.CERTIFICATE_READ,
    PERMISSIONS.CERTIFICATE_SHARE,
    PERMISSIONS.CERTIFICATE_RENDER,
    PERMISSIONS.FILE_UPLOAD,
    PERMISSIONS.FILE_DELETE,
    PERMISSIONS.AI_USE,
    PERMISSIONS.COURSE_AI_GENERATION,
    PERMISSIONS.ANNOUNCEMENT_READ,
    PERMISSIONS.ANNOUNCEMENT_CREATE,
    PERMISSIONS.ANNOUNCEMENT_DELETE,
    PERMISSIONS.NEWS_MANAGE,
    PERMISSIONS.NEWS_READ_PUBLIC,
    PERMISSIONS.ARTICLE_MANAGE,
    PERMISSIONS.ARTICLE_READ_PUBLIC,
    PERMISSIONS.QA_MANAGE,
    PERMISSIONS.QA_READ_PUBLIC,
    PERMISSIONS.REPORT_READ,
    PERMISSIONS.STATISTICS_READ_SELF,
    PERMISSIONS.STATISTICS_READ,
    PERMISSIONS.BILLING_CHECKOUT,
    PERMISSIONS.BILLING_MANAGE,
    PERMISSIONS.USER_MANAGE,
    PERMISSIONS.INTEGRATION_KEY_MANAGE,
    PERMISSIONS.INTEGRATION_API_USE,
    PERMISSIONS.TENANT_MANAGE,
    PERMISSIONS.COURSE_EXPORT,
    PERMISSIONS.ACTIVITY_LOG_READ
  ]
};
var SYSTEM_RULE_SET_SLUGS = {
  [SYSTEM_ROLE_SLUGS.STUDENT]: "student-default",
  [SYSTEM_ROLE_SLUGS.CONTENT_CREATOR]: "content_creator-default",
  [SYSTEM_ROLE_SLUGS.TRAINER]: "trainer-default",
  [SYSTEM_ROLE_SLUGS.ADMIN]: "admin-default"
};

// src/constants/qaSettings.ts
var ALLOWED_QA_SETTINGS = {
  QA_ENABLED: "QAEnabled",
  UNREGISTERED_USER_QA_ACCESSIBILITY: "unregisteredUserQAAccessibility"
};

// src/constants/registrationForm.ts
var FORM_TYPES = {
  REGISTRATION: "registration"
};
var FORM_FIELD_TYPES = /* @__PURE__ */ ((FORM_FIELD_TYPES2) => {
  FORM_FIELD_TYPES2["CHECKBOX"] = "checkbox";
  return FORM_FIELD_TYPES2;
})(FORM_FIELD_TYPES || {});

// src/constants/resourceLibrary.ts
var RESOURCE_LIBRARY_ASSET_TYPE = {
  IMAGE: "image",
  VIDEO: "video",
  PDF: "pdf",
  PRESENTATION: "presentation",
  DOCUMENT: "document",
  OTHER: "other"
};

// src/constants/scorm.ts
var SCORM_STANDARD = {
  SCORM_1_2: "scorm_1_2",
  SCORM_2004: "scorm_2004"
};
var SCORM_PACKAGE_ENTITY_TYPE = {
  COURSE: "course",
  LESSON: "lesson"
};
var SCORM_PACKAGE_STATUS = {
  PROCESSING: "processing",
  READY: "ready",
  FAILED: "failed"
};
var SCORM_IMPORT_ACTION = {
  CREATE_COURSE: "create-course",
  CREATE_LESSON: "create-lesson",
  ATTACH_LESSON_PACKAGE: "attach-lesson-package"
};
var SCORM_IMPORT_SOCKET = {
  EVENTS: {
    STATUS_CHANGED: "scorm-import-status-change"
  },
  MESSAGE_KEYS: {
    PROCESSING: "adminScorm.importProcessing.description",
    READY: "adminScorm.importSuccess.description",
    FAILED_COURSE: "adminScorm.importFailure.courseDescription",
    FAILED_LESSON: "adminScorm.importFailure.lessonDescription"
  }
};
var SCORM_COMPLETION_STATUS = {
  COMPLETED: "completed",
  INCOMPLETE: "incomplete",
  NOT_ATTEMPTED: "not_attempted",
  UNKNOWN: "unknown"
};
var SCORM_SUCCESS_STATUS = {
  PASSED: "passed",
  FAILED: "failed",
  UNKNOWN: "unknown"
};

// src/constants/sessionRevocation.ts
var SESSION_REVOCATION_SOCKET = {
  EVENTS: {
    PERMISSIONS_UPDATED: "permissions-updated"
  },
  REASONS: {
    ROLES_CHANGED: "roles_changed"
  },
  MESSAGE_KEYS: {
    PERMISSIONS_UPDATED: "sessionRevocation.permissionsUpdated"
  }
};

// src/constants/supportMode.ts
var SUPPORT_SESSION_STATUSES = {
  PENDING: "pending",
  ACTIVE: "active",
  REVOKED: "revoked"
};

// src/constants/tenant.ts
var TENANT_STATUSES = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};

// src/constants/theme.ts
var HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}){1,2}$/;

// src/constants/tus.ts
var DEFAULT_TUS_CHUNK_SIZE = 50 * 1024 * 1024;
var DEFAULT_TUS_TTL_MS = 4 * 60 * 60 * 1e3;

// src/constants/videoAutoplayActions.ts
var VIDEO_AUTOPLAY = {
  AUTOPLAY: "autoplay",
  AUTOPLAY_WITH_PLAY_NEXT: "autoplay_with_play_next",
  PLAY_NEXT: "play_next",
  NO_AUTOPLAY: "no_autoplay"
};

// src/constants/videoProviders.ts
var VIDEO_EMBED_PROVIDERS = {
  SELF: "self",
  YOUTUBE: "youtube",
  VIMEO: "vimeo",
  BUNNY: "bunny",
  UNKNOWN: "unknown"
};

// src/constants/voiceAction.ts
var VOICE_ACTION = {
  TRANSCRIPT: "transcript",
  VOICE_MENTOR: "voiceMentor"
};
var VOICE_SOCKET_EVENT = {
  START_AUDIO: "startAudio",
  AUDIO_STARTED: "audioStarted",
  AUDIO_CHUNK: "audioChunk",
  AUDIO_SPEECH: "audioSpeech",
  AUDIO_INTERRUPTED: "audioInterrupted",
  AUDIO_OUTPUT_COMPLETED: "audioOutputCompleted",
  MENTOR_TRANSCRIPTION: "mentorTranscription",
  MENTOR_RESPONSE_COMPLETED: "mentorResponseCompleted",
  STOP_AUDIO: "stopAudio",
  CANCEL_AUDIO: "cancelAudio",
  TRIGGER_TTS: "triggerTTS"
};

// src/constants/voiceModeState.ts
var VOICE_MODE_STATE = {
  IDLE: "idle",
  LISTENING: "listening",
  THINKING: "thinking",
  SPEAKING: "speaking"
};

// src/types/onboarding.ts
var OnboardingPages = /* @__PURE__ */ ((OnboardingPages2) => {
  OnboardingPages2["DASHBOARD"] = "dashboard";
  OnboardingPages2["COURSES"] = "courses";
  OnboardingPages2["ANNOUNCEMENTS"] = "announcements";
  OnboardingPages2["PROFILE"] = "profile";
  OnboardingPages2["SETTINGS"] = "settings";
  OnboardingPages2["PROVIDER_INFORMATION"] = "providerInformation";
  return OnboardingPages2;
})(OnboardingPages || {});

// src/types/videoUploadTypes.ts
var VIDEO_UPLOAD_STATUS = {
  QUEUED: "queued",
  UPLOADED: "uploaded",
  PROCESSED: "processed",
  FAILED: "failed"
};
var VIDEO_PROVIDERS = {
  BUNNY: "bunny",
  S3: "s3"
};

// src/utils/certificate.ts
var defaultCertificateRenderTheme = {
  titleColor: "#1f2937",
  certifyTextColor: "#374151",
  nameColor: "#1f2937",
  courseNameColor: "#1f2937",
  bodyTextColor: "#4b5563",
  labelTextColor: "#1f2937",
  lineColor: "#9ca3af",
  logoColor: "#1f2937"
};
var CERTIFICATE_KIND = {
  COURSE: "course",
  LEARNING_PATH: "learningPath"
};
var certificateTranslations = {
  pl: {
    certificate: "CERTYFIKAT",
    courseCompletion: "UKO\u0143CZENIA KURSU",
    certifyThat: "NINIEJSZYM ZA\u015AWIADCZA SI\u0118, \u017BE",
    successfulCompletion: {
      [CERTIFICATE_KIND.COURSE]: "uko\u0144czy\u0142/a kurs",
      [CERTIFICATE_KIND.LEARNING_PATH]: "uko\u0144czy\u0142/a \u015Bcie\u017Ck\u0119 rozwoju"
    },
    confirmation: "potwierdzaj\u0105c tym samym realizacj\u0119 programu szkoleniowego.",
    date: "Data",
    signature: "Podpis"
  },
  en: {
    certificate: "CERTIFICATE",
    courseCompletion: "OF COURSE COMPLETION",
    certifyThat: "THIS IS TO CERTIFY THAT",
    successfulCompletion: {
      [CERTIFICATE_KIND.COURSE]: "has successfully completed the course",
      [CERTIFICATE_KIND.LEARNING_PATH]: "has successfully completed the development path"
    },
    confirmation: "thereby confirming participation in the full training program.",
    date: "Date",
    signature: "Signature"
  },
  de: {
    certificate: "ZERTIFIKAT",
    courseCompletion: "KURSABSCHLUSS",
    certifyThat: "HIERMIT WIRD BEST\xC4TIGT, DASS",
    successfulCompletion: {
      [CERTIFICATE_KIND.COURSE]: "hat den Kurs erfolgreich abgeschlossen",
      [CERTIFICATE_KIND.LEARNING_PATH]: "hat den Entwicklungspfad erfolgreich abgeschlossen"
    },
    confirmation: "und best\xE4tigt damit die Teilnahme am gesamten Schulungsprogramm.",
    date: "Datum",
    signature: "Unterschrift"
  },
  lt: {
    certificate: "SERTIFIKATAS",
    courseCompletion: "KURSO PABAIGOS",
    certifyThat: "\u0160IUO PATVIRTINAMA, KAD",
    successfulCompletion: {
      [CERTIFICATE_KIND.COURSE]: "s\u0117kmingai baig\u0117 kurs\u0105",
      [CERTIFICATE_KIND.LEARNING_PATH]: "s\u0117kmingai baig\u0117 tobul\u0117jimo keli\u0105"
    },
    confirmation: "taip patvirtindamas dalyvavim\u0105 visoje mokymo programoje.",
    date: "Data",
    signature: "Para\u0161as"
  },
  cs: {
    certificate: "CERTIFIK\xC1T",
    courseCompletion: "UKON\u010CEN\xCD KURZU",
    certifyThat: "T\xCDMTO SE POTVRZUJE, \u017DE",
    successfulCompletion: {
      [CERTIFICATE_KIND.COURSE]: "\xFAsp\u011B\u0161n\u011B absolvoval/a kurz",
      [CERTIFICATE_KIND.LEARNING_PATH]: "\xFAsp\u011B\u0161n\u011B dokon\u010Dil/a rozvojovou cestu"
    },
    confirmation: "\u010D\xEDm\u017E potvrzuje \xFA\u010Dast na cel\xE9m \u0161kolic\xEDm programu.",
    date: "Datum",
    signature: "Podpis"
  }
};
function buildCertificateMarkup(options) {
  const {
    studentName = "",
    courseName = "",
    completionDate = "",
    platformLogoUrl,
    signatureImageUrl,
    backgroundImageUrl,
    lang = "en",
    colorTheme = defaultCertificateRenderTheme,
    isDownload = false,
    certificateKind = CERTIFICATE_KIND.COURSE
  } = options;
  const t = certificateTranslations[lang];
  const backgroundStyle = [
    backgroundImageUrl ? `background-image:url(${escapeCssUrl(backgroundImageUrl)});background-size:100% 100%;background-position:center;background-repeat:no-repeat;` : "background-color:white;"
  ].join("");
  const logoMarkup = platformLogoUrl ? `<img src="${escapeHtml(platformLogoUrl)}" alt="Platform Logo" class="aspect-auto h-11" />` : `<div class="h-11" style="color:${escapeHtml(colorTheme.logoColor)};">${APP_LOGO_SVG}</div>`;
  const signatureImageMarkup = signatureImageUrl ? `<img src="${escapeHtml(signatureImageUrl)}" alt="${escapeHtml(t.signature)}" class="mb-2 h-16 w-full object-contain" />` : "";
  const certificateBodyMarkup = `<div
    class="flex h-full w-full flex-col items-center justify-center gap-y-12 px-14 py-12"
    style="${backgroundStyle}"
  >
    ${logoMarkup}
    <div class="flex flex-col items-center justify-center gap-y-4">
      <p
        class="text-[62px] font-black uppercase tracking-wider"
        style="color:${escapeHtml(colorTheme.titleColor)};"
      >
        ${escapeHtml(t.certificate)}
      </p>
      <p
        class="text-[17px] font-semibold uppercase tracking-[0.2em]"
        style="color:${escapeHtml(colorTheme.certifyTextColor)};"
      >
        ${escapeHtml(t.certifyThat)}
      </p>
    </div>

    <div class="flex flex-col items-center justify-center">
      <p
        class="mb-4 text-[44px] tracking-wider"
        style="color:${escapeHtml(colorTheme.nameColor)};"
      >
        ${escapeHtml(studentName)}
      </p>
      <p
        class="text-[18px] text-gray-600"
        style="color:${escapeHtml(colorTheme.bodyTextColor)};"
      >
        ${escapeHtml(t.successfulCompletion[certificateKind])}
      </p>
      <p
        class="text-[24px]"
        style="color:${escapeHtml(colorTheme.courseNameColor)};"
      >
        "${escapeHtml(courseName)}"
      </p>
      <p
        class="text-[18px] text-gray-600"
        style="color:${escapeHtml(colorTheme.bodyTextColor)};"
      >
        ${escapeHtml(t.confirmation)}
      </p>
    </div>

    <div class="flex items-end gap-x-52">
      <div class="flex w-[280px] flex-col items-center">
        <p
          class="text-[18px] text-gray-600"
          style="color:${escapeHtml(colorTheme.bodyTextColor)};"
        >
          ${escapeHtml(completionDate)}
        </p>
        <hr
          class="mx-auto mb-3 w-full"
          style="border-color:${escapeHtml(colorTheme.lineColor)};"
        />
        <p
          class="text-[18px] uppercase text-gray-800"
          style="color:${escapeHtml(colorTheme.labelTextColor)};"
        >
          ${escapeHtml(t.date)}
        </p>
      </div>

      <div class="flex w-[280px] flex-col items-center">
        ${signatureImageMarkup}
        <hr
          class="mx-auto mb-3 w-full"
          style="border-color:${escapeHtml(colorTheme.lineColor)};"
        />
        <p
          class="text-[18px] uppercase text-gray-800"
          style="color:${escapeHtml(colorTheme.labelTextColor)};"
        >
          ${escapeHtml(t.signature)}
        </p>
      </div>
    </div>
  </div>`;
  if (!isDownload) return certificateBodyMarkup;
  return `<div class="mx-auto h-[210mm] w-[297mm] overflow-hidden">${certificateBodyMarkup}</div>`;
}
function buildCertificateHtmlDocument(html, options) {
  const baseTag = buildBaseTag(options?.baseUrl);
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    ${baseTag}
    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      html, body {
        width: 297mm;
        height: 210mm;
        overflow: hidden;
        background-color: white;
      }
      body > * {
        width: 297mm;
        height: 210mm;
        transform-origin: top left;
      }
    </style>
    <title>Certificate</title>
    <script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>
  </head>
  <body>
    ${html}
  </body>
</html>`;
}
function buildBaseTag(baseUrl) {
  if (!baseUrl) return "";
  return `<base href="${escapeHtml(baseUrl)}" />`;
}
var APP_LOGO_SVG = `<svg viewBox="0 0 366 80" fill="none" xmlns="http://www.w3.org/2000/svg" style="height:100%;width:auto;display:block;"><path d="M41.2045 26.686C35.1989 26.686 30.3109 29.4993 28.0114 34.914C25.6412 29.5058 20.7468 26.686 14.8182 26.686C6.37177 26.686 0 31.9465 0 42.84V64.2546H4.9651V42.84C4.9651 35.0617 9.26219 31.2785 15.1908 31.2785C21.1193 31.2785 25.5641 35.203 25.5641 42.84V64.2546H30.5292V42.84C30.5292 35.2094 34.9034 31.2785 40.832 31.2785C46.7606 31.2785 51.0576 35.0553 51.0576 42.84V64.2546H56.0227V42.84C56.0227 31.9465 49.651 26.686 41.2045 26.686Z" fill="#292929"/><path d="M73.7249 29.2379C63.7112 35.0187 60.7308 46.1498 66.2868 55.7844C71.8429 65.4126 82.9806 68.3994 92.9943 62.6186C100.375 58.3537 103.927 51.1727 102.995 43.921L98.3127 46.6252C98.3705 51.4682 95.7113 55.7459 90.7013 58.6363C83.578 62.7471 75.9344 60.9165 71.541 54.6411L101.126 37.5558L100.31 36.1427C94.7543 26.5145 83.745 23.4571 73.7313 29.2379H73.7249ZM69.4342 50.7038C66.8007 43.921 69.4085 37.0291 76.0243 33.2074C82.6402 29.3856 89.6735 30.4583 94.401 36.2904L69.4342 50.7038Z" fill="#292929"/><path d="M128.649 26.686C117.833 26.686 110.568 32.9871 110.568 45.1395V64.261H115.533V45.1395C115.533 36.0958 120.723 31.285 128.649 31.285C136.575 31.285 141.765 36.1023 141.765 45.1395V64.261H146.73V45.1395C146.73 32.9871 139.466 26.686 128.649 26.686Z" fill="#292929"/><path d="M179.63 50.7748C179.63 57.5191 175.853 60.6278 170.22 60.6278C164.587 60.6278 160.81 57.442 160.81 50.7748V32.1736H182.372V27.6518H160.81V14.6836H155.845V50.7748C155.845 60.4094 161.696 65.2267 170.22 65.2267C178.743 65.2267 184.447 60.4094 184.447 50.7748V49.9591H179.63V50.7748Z" fill="#292929"/><path d="M227.431 26.686C216.614 26.686 209.35 32.9871 209.35 45.1395V64.261H214.315V45.1395C214.315 36.0958 219.505 31.285 227.431 31.285C235.357 31.285 240.547 36.1023 240.547 45.1395V64.261H245.512V45.1395C245.512 32.9871 238.247 26.686 227.431 26.686Z" fill="#292929"/><path d="M319.841 26.686C308.279 26.686 300.128 34.8369 300.128 45.9552C300.128 57.0736 308.279 65.2245 319.841 65.2245C331.403 65.2245 339.553 57.0736 339.553 45.9552C339.553 34.8369 331.403 26.686 319.841 26.686ZM319.841 60.6255C311.099 60.6255 305.17 54.4016 305.17 45.9552C305.17 37.5089 311.099 31.285 319.841 31.285C328.583 31.285 334.511 37.5089 334.511 45.9552C334.511 54.4016 328.583 60.6255 319.841 60.6255Z" fill="#292929"/><path d="M272.817 26.686C261.255 26.686 253.104 34.8369 253.104 45.9552C253.104 57.0736 261.255 65.2245 272.817 65.2245C284.379 65.2245 292.53 57.0736 292.53 45.9552C292.53 34.8369 284.379 26.686 272.817 26.686ZM272.817 60.6255C264.075 60.6255 258.146 54.4016 258.146 45.9552C258.146 37.5089 264.075 31.285 272.817 31.285C281.559 31.285 287.487 37.5089 287.487 45.9552C287.487 54.4016 281.559 60.6255 272.817 60.6255Z" fill="#292929"/><path d="M272.817 75.034C267.852 75.034 263.234 73.5374 259.393 70.9746L256.643 75.1111C261.275 78.2006 266.837 79.999 272.817 79.999C278.797 79.999 284.359 78.1941 288.99 75.1111L286.241 70.9746C282.4 73.5374 277.782 75.034 272.817 75.034Z" fill="#292929"/><path d="M192.598 26.6894C192.598 24.5376 194.23 22.9126 196.375 22.9126C198.521 22.9126 200.152 24.5441 200.152 26.6894C200.152 28.8347 198.521 30.4661 196.375 30.4661C194.23 30.4661 192.598 28.8347 192.598 26.6894Z" fill="#292929"/><path d="M196.381 36.8942C195.534 36.8942 194.718 36.785 193.934 36.5988V64.2694H198.899V36.5859C198.096 36.7851 197.255 36.9007 196.381 36.9007V36.8942Z" fill="#292929"/><path d="M272.823 31.2612V26.6944H290.16C290.16 29.2187 288.111 31.2612 285.593 31.2612H272.823Z" fill="#292929"/><path d="M350.993 34.3951C350.993 25.9488 356.922 19.7248 365.664 19.7248V15.1323C354.102 15.1323 345.951 23.2832 345.951 34.4015H350.993V34.3951Z" fill="#4796FD"/><path d="M331.223 19.7257C339.669 19.7257 345.893 25.6542 345.893 34.396H350.486C350.486 22.8345 342.335 14.6836 331.216 14.6836V19.7257H331.223Z" fill="#4796FD"/><path d="M365.664 14.6771C357.217 14.6771 350.993 8.74864 350.993 0.00683594H346.401C346.401 11.5684 354.551 19.7192 365.67 19.7192V14.6771H365.664Z" fill="#4796FD"/><path d="M345.893 0.00640974C345.893 8.45275 339.964 14.6767 331.222 14.6767V19.2692C342.784 19.2692 350.935 11.1183 350.935 0H345.893V0.00640974Z" fill="#4796FD"/></svg>`;
function escapeHtml(value) {
  return value.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;").replace('"', "&quot;").replace("'", "&#39;");
}
function escapeCssUrl(value) {
  return value.replace("\\", "\\\\").replace('"', '\\"').replace("\n", "");
}

// src/utils/permissions.ts
var hasPermission = (permissions, permission) => {
  if (!permissions?.length) return false;
  return permissions.includes(permission);
};
var hasAnyPermission = (permissions, requiredPermissions) => {
  if (!requiredPermissions.length) return true;
  if (!permissions?.length) return false;
  return requiredPermissions.some((permission) => permissions.includes(permission));
};
var hasAllPermissions = (permissions, requiredPermissions) => {
  if (!requiredPermissions.length) return true;
  if (!permissions?.length) return false;
  return requiredPermissions.every((permission) => permissions.includes(permission));
};
var matchesRequirement = (permissions, requirement) => {
  if (!requirement) return true;
  const { anyOf = [], allOf = [] } = requirement;
  return hasAnyPermission(permissions, anyOf) && hasAllPermissions(permissions, allOf);
};

// src/utils/videoUrls.ts
var UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
var tryParseUrl = (input) => {
  try {
    return new URL(input);
  } catch {
    try {
      return new URL(input, "http://localhost");
    } catch {
      return null;
    }
  }
};
var extractYoutubeId = (url) => {
  if (url.hostname === "youtu.be") {
    return url.pathname.replace("/", "").split("/")[0] || null;
  }
  if (url.pathname === "/watch") {
    return url.searchParams.get("v");
  }
  const pathParts = url.pathname.split("/").filter(Boolean);
  const embedIndex = pathParts.findIndex((part) => part === "embed" || part === "shorts");
  if (embedIndex !== -1 && pathParts[embedIndex + 1]) {
    return pathParts[embedIndex + 1];
  }
  return null;
};
var extractVimeoId = (url) => {
  const pathParts = url.pathname.split("/").filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1];
  if (!lastPart) return null;
  return /^\d+$/.test(lastPart) ? lastPart : null;
};
var extractBunnyId = (sourceUrl, url) => {
  if (sourceUrl.startsWith("bunny-")) return sourceUrl.replace("bunny-", "");
  if (!url) return null;
  const pathParts = url.pathname.split("/").filter(Boolean);
  if (url.hostname === "iframe.mediadelivery.net") {
    const embedIndex = pathParts.findIndex((part) => part === "embed");
    if (embedIndex !== -1 && pathParts[embedIndex + 2]) {
      return pathParts[embedIndex + 2];
    }
  }
  const uuidSegment = pathParts.find((part) => UUID_REGEX.test(part));
  if (uuidSegment) return uuidSegment.match(UUID_REGEX)?.[0] ?? null;
  if (pathParts.length >= 2 && pathParts[pathParts.length - 1].endsWith(".jpg")) {
    return pathParts[pathParts.length - 2] ?? null;
  }
  return pathParts[pathParts.length - 1] ?? null;
};
var detectVideoProviderFromUrl = (sourceUrl) => {
  const url = tryParseUrl(sourceUrl);
  if (!url) return VIDEO_EMBED_PROVIDERS.UNKNOWN;
  if (extractResourceIdFromSourceUrl(sourceUrl)) {
    return VIDEO_EMBED_PROVIDERS.SELF;
  }
  const host = url.hostname.toLowerCase();
  if (host === "youtu.be" || host.endsWith("youtube.com")) return VIDEO_EMBED_PROVIDERS.YOUTUBE;
  if (host.endsWith("vimeo.com")) return VIDEO_EMBED_PROVIDERS.VIMEO;
  if (host.endsWith("bunny.net") || host.endsWith("bunnycdn.com") || host.includes("b-cdn") || host.includes("mediadelivery")) {
    return VIDEO_EMBED_PROVIDERS.BUNNY;
  }
  return VIDEO_EMBED_PROVIDERS.UNKNOWN;
};
var extractVideoIdFromSourceUrl = (sourceUrl, provider) => {
  const url = tryParseUrl(sourceUrl);
  switch (provider) {
    case VIDEO_EMBED_PROVIDERS.YOUTUBE:
      return url ? extractYoutubeId(url) : null;
    case VIDEO_EMBED_PROVIDERS.VIMEO:
      return url ? extractVimeoId(url) : null;
    case VIDEO_EMBED_PROVIDERS.BUNNY:
      return extractBunnyId(sourceUrl, url);
    default:
      return null;
  }
};
var extractResourceIdFromSourceUrl = (sourceUrl) => {
  if (UUID_REGEX.test(sourceUrl)) return sourceUrl.match(UUID_REGEX)?.[0] ?? null;
  const url = tryParseUrl(sourceUrl);
  if (!url) return null;
  const match = url.pathname.match(/\/(?:lesson|articles|news)-resource\/([0-9a-f-]{36})/i);
  if (match?.[1]) return match[1];
  const pathParts = url.pathname.split("/").filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1];
  if (lastPart && UUID_REGEX.test(lastPart)) return lastPart;
  return null;
};
export {
  ACCESS_GUARD,
  AI_MENTOR_TTS_PRESET,
  AI_MENTOR_TYPE,
  AI_MENTOR_VOICE_MODE,
  ALLOWED_AGE_LIMITS,
  ALLOWED_ARTICLES_SETTINGS,
  ALLOWED_AVATAR_IMAGE_TYPES,
  ALLOWED_CERTIFICATE_SIGNATURE_FILE_TYPES,
  ALLOWED_EXCEL_FILE_TYPES,
  ALLOWED_EXTENSIONS,
  ALLOWED_LESSON_IMAGE_FILE_TYPES,
  ALLOWED_NEWS_SETTINGS,
  ALLOWED_PDF_FILE_TYPES,
  ALLOWED_PRESENTATION_FILE_TYPES,
  ALLOWED_QA_SETTINGS,
  ALLOWED_VIDEO_FILE_TYPES,
  ALLOWED_WORD_FILE_TYPES,
  ANNOUNCEMENT_EMAIL_TEMPLATES,
  ANNOUNCEMENT_SOURCE_TYPES,
  ANNOUNCEMENT_STATUSES,
  ARTICLE_STATUS,
  CALENDAR_EVENT_SOURCE_ROLES,
  CALENDAR_EVENT_SOURCE_TYPES,
  CALENDAR_EVENT_STATUSES,
  CERTIFICATE_ARCHIVE_REASONS,
  CERTIFICATE_KIND,
  CERTIFICATE_RESET_SCOPES,
  CERTIFICATE_STATUSES,
  CERTIFICATE_VALIDITY_TYPES,
  CERTIFICATE_VALIDITY_UNITS,
  COURSE_CHAT_ALLOWED_REACTIONS,
  COURSE_CHAT_SOCKET_EVENTS,
  COURSE_ENROLLMENT,
  COURSE_FEATURE,
  COURSE_FEATURE_ERROR_TRANSLATION_KEY,
  COURSE_ORIGIN_TYPES,
  COURSE_PROGRESS_STATUSES,
  COURSE_STATUSES,
  COURSE_TYPE,
  COURSE_TYPE_DISABLED_FEATURES,
  DEFAULT_LIVE_TRAINING_SETTINGS,
  DEFAULT_TUS_CHUNK_SIZE,
  DEFAULT_TUS_TTL_MS,
  ENTITY_TYPES,
  FEATURES,
  FEATURE_SETTINGS_KEYS,
  FORM_FIELD_TYPES,
  FORM_TYPES,
  HEX_COLOR_REGEX,
  LEARNING_PATH_CERTIFICATE_STATUSES,
  LEARNING_PATH_ENROLLMENT_TYPES,
  LEARNING_PATH_ENTITY_TYPES,
  LEARNING_PATH_PROGRESS_STATUSES,
  LEARNING_PATH_STATUSES,
  LESSON_TYPES,
  LIVE_TRAINING_DELIVERY_TYPES,
  LIVE_TRAINING_DESCRIPTION_MAX_LENGTH,
  LIVE_TRAINING_LINK_ENTITY_TYPES,
  LIVE_TRAINING_MAX_PARTICIPANTS_LIMIT,
  LIVE_TRAINING_MEMBER_ROLES,
  LIVE_TRAINING_PARTICIPANT_ROLES,
  LIVE_TRAINING_RESOURCE_RELATIONSHIP_TYPES,
  LIVE_TRAINING_SESSION_STATUSES,
  LIVE_TRAINING_SOCKET_CLIENT_EVENTS,
  LIVE_TRAINING_SOCKET_ROOMS,
  LIVE_TRAINING_SOCKET_ROOM_BUILDERS,
  LIVE_TRAINING_SOCKET_SERVER_EVENTS,
  LIVE_TRAINING_STATUSES,
  LIVE_TRAINING_TITLE_MAX_LENGTH,
  LIVE_TRAINING_VISIBILITY_SCOPES,
  LOGIN_PAGE_DOCUMENTS_FILE_TYPES,
  LUMA_FILE_INGESTION_ALLOWED_EXTENSIONS,
  LUMA_FILE_INGESTION_ALLOWED_MIME_TYPES,
  LUMA_FILE_INGESTION_MAX_SIZE_BYTES,
  LUMA_FILE_INGESTION_MAX_SIZE_MB,
  MASTER_COURSE_ENTITY_TYPES,
  MASTER_COURSE_EXPORT_SYNC_STATUSES,
  MAX_LOGIN_PAGE_DOCUMENTS,
  OnboardingPages,
  PERMISSIONS,
  RESOURCE_LIBRARY_ASSET_TYPE,
  SCORM_COMPLETION_STATUS,
  SCORM_IMPORT_ACTION,
  SCORM_IMPORT_SOCKET,
  SCORM_PACKAGE_ENTITY_TYPE,
  SCORM_PACKAGE_STATUS,
  SCORM_STANDARD,
  SCORM_SUCCESS_STATUS,
  SESSION_REVOCATION_SOCKET,
  SHARE_IMAGE_HEIGHT,
  SHARE_IMAGE_WIDTH,
  SUPPORTED_LANGUAGES,
  SUPPORT_SESSION_STATUSES,
  SYSTEM_ROLE_PERMISSIONS,
  SYSTEM_ROLE_SLUGS,
  SYSTEM_RULE_SET_SLUGS,
  TENANT_STATUSES,
  VIDEO_AUTOPLAY,
  VIDEO_EMBED_PROVIDERS,
  VIDEO_PROVIDERS,
  VIDEO_UPLOAD_STATUS,
  VOICE_ACTION,
  VOICE_MODE_STATE,
  VOICE_SOCKET_EVENT,
  buildCertificateHtmlDocument,
  buildCertificateMarkup,
  defaultCertificateRenderTheme,
  detectVideoProviderFromUrl,
  extractResourceIdFromSourceUrl,
  extractVideoIdFromSourceUrl,
  extractVimeoId,
  extractYoutubeId,
  getCourseChatRoom,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  isCourseFeatureEnabledForCourseType,
  matchesRequirement,
  tryParseUrl
};
